/* Bundle of helper methods for managing timeline-related operations.*/

import {api} from "@/shared/api";
import {RecordDialog} from "@/shared/recording/recordDialog";
import {htmlEscape} from "@/shared/utils/string";
import {
	getWorkingTimeTypesForAddAbsence,
	getWorkingTimeTypesForAddWorkingTime,
	isProjectTimeDateTimeChangeAllowed,
	isProjectTimeEditAllowed,
	isProjectTimeTaskLocked,
	isRecordingModeUserAllowed,
	isWorkingTimeDateTimeChangeAllowed,
	isWorkingTimeEditAllowed
} from "@/shared/securityHelper";
import {CombinedTimelineDayComponentConfig, WorkingTimeTimelineItem, WorkingTimeTimelineItemType} from "./timelineTypes";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {formatCustomField} from "@/shared/utils/misc";
import {
	formatDate,
	formatDateTime,
	formatTime,
	isSameDay,
	parseISODate,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {
	CombinedRecordingStatus,
	CustomFieldDefinition,
	ProjectTime,
	Record,
	ReportWorkingTime,
	RunningEntry,
	RunningProjectTime,
	RunningWorkingTime,
	ValidationResult,
	WorkingTime,
	WorkingTimeRequestResponseModel
} from "@/shared/timr";
import {
	DurationUnit,
	RecordedEntryStatus,
	RecordingMode,
	RecordingProcedure,
	WorkingTimeTypeCategory,
	WorkingTimeTypeRecordingMode
} from "@/shared/timrTypes";
import {getHighestSeverity} from "@/shared/validations/validationUtil";
import {mapWorkingTimeToReportWorkingTime, toRecordedEntryStatus} from "@/shared/utils/recordMapper";
import {getWorkingTimeOrigin, WorkingTimeOrigin} from "@/shared/recording/workingTimeOrigin";

/**
 * Generates a date and time object to preset the date fields in an "Add Recording" dialog
 * Uses the provided date with the hour:minutes of the current (now) time as start adds <code>DEFAULT_DURATION</code> (+ 30 min) for the end time
 *
 * @param date - The base date to calculate the preset start and end times.
 * @return An object containing the start and end date objects.
 */
export function getDialogPresetDateTime(date: Temporal.ZonedDateTime | Temporal.PlainDate | null): {
	start: Temporal.ZonedDateTime,
	end: Temporal.ZonedDateTime
} {
	const now = Temporal.Now.zonedDateTimeISO();
	// Use the provided date's calendar date but the current wall-clock time in the local timezone.
	const plainDate = date instanceof Temporal.ZonedDateTime ? date.toPlainDate() : (date ?? now.toPlainDate());
	const start = plainDate.toZonedDateTime({
		timeZone: now.timeZoneId,
		plainTime: now.toPlainTime().with({second: 0, millisecond: 0, microsecond: 0, nanosecond: 0})
	});
	const end = start.add({milliseconds: RecordDialog.DEFAULT_DURATION});

	return {start, end};
}

/* ----------------------------------------- resume ----------------------------------------- */
/**
 * Resumes a working time recording (if resumable)
 *
 * @param status The combined recording status containing the current state of working and project time recordings.
 * @param item The working time timeline item to be resumed.
 * @param workingTime The working time record to be resumed.
 * @returns A `JQueryPromise` that resolves when the working time is successfully resumed, or `undefined` if the operation is not allowed.
 */
export function resumeWorkingTime(status: CombinedRecordingStatus, item: WorkingTimeTimelineItem, workingTime: WorkingTime): JQuery.jqXHR<void> | null {
	if (!isWorkingTimeResumable(status, item, workingTime)) {
		return;
	}

	return api("timeline/resumeCombinedRecording.json")
		.post({
			procedure: RecordingProcedure.WorkingTime,
			workingTimeId: workingTime.id,
			timestamp: Date.now()
		});
}

export function deleteProjectTime(projectTime: ProjectTime): JQueryDeferred<boolean> {
	if (!isProjectTimeDeletable(projectTime)) {
		return;
	}

	const deferred: JQueryDeferred<boolean> = $.Deferred();
	BootstrapModal.confirm({
		title: "projectTime.delete.title",
		message: messages.withArguments(
			"projectTime.delete.message",
			projectTime.task.name,
			projectTime.description ? " " + messages.withArguments("record.delete.message.description", projectTime.description) : "",
			formatDateTime(parseISOZonedDateTime(projectTime.start))
		),
		yesLabel: "confirmation.delete",
		noLabel: "confirmation.cancel",
		yesAction: (modal: BootstrapModal.Modal) => {
			api("recording/deleteProjectTime.json")
				.post({
					id: projectTime.id
				})
				.done(() => {
					deferred.resolve(true);
					modal.close();
				})
				.fail(() => {
					deferred.reject();
					modal.close();
				});
		}
	});
	return deferred;
}

export function deleteWorkingTime(workingTime: WorkingTime): JQueryDeferred<boolean> {
	if (!isWorkingTimeDeletable(workingTime)) {
		return;
	}

	const deferred: JQueryDeferred<boolean> = $.Deferred();

	if (workingTime.workingTimeDateSpanId != null) {
		api("workingTimeDateSpans/findWorkingTimeDateSpanByWorkingTimeId.json")
			.get({workingTimeId: workingTime.id})
			.done((workingTimeDateSpan) => {
				BootstrapModal.confirm({
					title: "workingtime.delete.title",
					message: messages.withArguments("workingTimeDateSpans.dialog.delete.message",
						workingTimeDateSpan.workingTimeType.name,
						formatDate(parseISODate(workingTimeDateSpan.start)),
						formatDate(parseISODate(workingTimeDateSpan.end))
					),
					yesLabel: "confirmation.delete",
					noLabel: "confirmation.cancel",
					yesAction: (modal: BootstrapModal.Modal) => {
						api("workingTimeDateSpans/deleteWorkingTimeDateSpan.json")
							.post({workingTimeDateSpanId: workingTimeDateSpan.id})
							.done(() => {
								deferred.resolve(true);
								modal.close();
							})
							.fail(() => {
								deferred.reject();
								modal.close();
							});
					}
				});
			})
			.fail(() => deferred.reject());
	} else {
		BootstrapModal.confirm({
			title: "workingtime.delete.title",
			message: messages.withArguments(
				"workingtime.delete.message",
				workingTime.workingTimeType.name,
				workingTime.description ? " " + messages.withArguments("record.delete.message.description", workingTime.description) : "",
				formatDateTime(parseISOZonedDateTime(workingTime.start))
			),
			yesLabel: "confirmation.delete",
			noLabel: "confirmation.cancel",
			yesAction: (modal: BootstrapModal.Modal) => {
				api("recording/deleteWorkingTime.json")
					.post({id: workingTime.id})
					.done(() => {
						deferred.resolve(true);
						modal.close();
					})
					.fail(() => {
						deferred.reject();
						modal.close();
					});
			}
		});
	}

	return deferred;
}

/**
 * Resumes a project time recording (if resumable)
 *
 * There are two cases:
 * 1. Nothing is running, selected project time is in attendance time -> resume both
 * 2. Attendance time is running, selected project time is in attendance time -> resume project time
 *
 * @param status The combined recording status containing the current state of working and project time recordings.
 * @param item The working time timeline item associated with the project time to be resumed.
 * @param projectTime The project time record to be resumed.
 * @returns A `JQueryPromise` that resolves when the project time is successfully resumed, or `undefined` if the operation is not allowed.
 */
export function resumeProjectTime(status: CombinedRecordingStatus, item: WorkingTimeTimelineItem, projectTime: ProjectTime): JQuery.jqXHR<void> | null {
	if (!isProjectTimeResumable(status, item, projectTime)) {
		return;
	}

	if (globals.recordingMode === RecordingMode.CLASSIC) {
		return api("timeline/resumeCombinedRecording.json")
			.post({
				procedure: RecordingProcedure.ProjectTime,
				projectTimeId: projectTime.id,
				timestamp: Date.now()
			});
	} else if (isTimelineSubitemProjectTimeInAttendanceTime(item, projectTime)) {
		if (isNothingRunning(status)) {
			return api("timeline/resumeCombinedRecording.json")
				.post({
					procedure: RecordingProcedure.Both,
					workingTimeId: item.workingTime.id,
					projectTimeId: projectTime.id,
					timestamp: Date.now()
				});
		}

		if (isAttendanceTimeRunning(status)) {
			return api("timeline/resumeCombinedRecording.json")
				.post({
					procedure: RecordingProcedure.ProjectTime,
					projectTimeId: projectTime.id,
					timestamp: Date.now()
				});
		}
	}
}

export function showProjectTimeResumeBothMessage(status: CombinedRecordingStatus, item: WorkingTimeTimelineItem, projectTime: ProjectTime): boolean {
	return isProjectTimeResumable(status, item, projectTime) &&
		isTimelineSubitemProjectTimeInAttendanceTime(item, projectTime) &&
		isNothingRunning(status);
}

/**
 * Determines if a working time recording is resumable based on the current combined recording status, the timeline item and working time recording.
 *
 * @param combinedRecordingStatus The combined recording status containing the current state of working and project time recordings.
 * @param workingTimeItem The working time timeline item to be checked.
 * @param workingTime The working time record to be checked.
 * @returns `true` if the working time is resumable, otherwise `false`.
 */
export function isWorkingTimeResumable(combinedRecordingStatus: CombinedRecordingStatus, workingTimeItem: WorkingTimeTimelineItem, workingTime: WorkingTime): boolean {
	// verify whether the recording can be continued in principle (working time is active, date time is changeable and resumable)
	if (combinedRecordingStatus.workingTimeStatus === null || !isWorkingTimeDateTimeChangeAllowed() ||
		!isRecordingResumable(combinedRecordingStatus?.workingTimeStatus?.lastWorkingTime, combinedRecordingStatus?.workingTimeStatus?.runningWorkingTime, workingTime)) {
		return false;
	}

	if (globals.recordingMode === RecordingMode.CLASSIC) {
		return !isWorkingTimeRunning(combinedRecordingStatus);
	} else if (globals.recordingMode === RecordingMode.STRICT) {
		return isNothingRunning(combinedRecordingStatus) && isTimelineRecordingNonAttendanceTime(workingTimeItem);
	} else if (globals.recordingMode === RecordingMode.COMBINED) {
		return isNothingRunning(combinedRecordingStatus) && (isTimelineRecordingAttendanceTime(workingTimeItem) || isTimelineRecordingNonAttendanceTime(workingTimeItem));
	} else {
		return false;
	}
}

/**
 * Determines whether a working time timeline item's "Edit" context menu entry should be active,
 * depending on the dialog the working time is opened in (see {@link getWorkingTimeOrigin}).
 */
export function isWorkingTimeEditable(workingTime: WorkingTime): boolean {
	switch (getWorkingTimeOrigin(workingTime)) {
		case WorkingTimeOrigin.Request:
			// a working time created from a request is only ever edited through the request itself, not directly
			return false;
		case WorkingTimeOrigin.DateSpan:
			return isWorkingTimeDateSpanEditable(mapWorkingTimeToReportWorkingTime(workingTime));
		case WorkingTimeOrigin.Standalone:
			return isWorkingTimeEditAllowed(mapWorkingTimeToReportWorkingTime(workingTime));
	}
}

/**
 * Returns whether clicking a timeline working time should open the linked WorkingTimeDateSpan in edit mode.
 *
 * Edit is allowed when:
 * - the working time itself is still changeable,
 * - an existing date span has not already become locked because one of its underlying working times is no longer editable,
 * - and the current date/time policy still allows editing this kind of entry.
 */
export function isWorkingTimeDateSpanEditable(workingTime: ReportWorkingTime): boolean {
	return workingTime.status === RecordedEntryStatus.Changeable &&
		(workingTime.workingTimeDateSpanId === null || workingTime.workingTimeDateSpanEditable) &&
		// recordingMode gates direct edits/deletes regardless of editUnit
		workingTime.workingTimeTypeRecordingModeUser !== WorkingTimeTypeRecordingMode.BY_REQUEST &&
		(isRecordingModeUserAllowed(workingTime.workingTimeTypeRecordingModeUser) || globals.isGroupLeader) &&
		(workingTime.editUnit !== DurationUnit.Minutes || isWorkingTimeDateTimeChangeAllowed());
}

export function isWorkingTimeDuplicateable(workingTime: WorkingTime) {
	const recordingMode = workingTime.workingTimeType.recordingModeUser;
	return !workingTime.hasWorkingTimeRequest &&
		recordingMode !== WorkingTimeTypeRecordingMode.BY_REQUEST &&
		(isRecordingModeUserAllowed(recordingMode) || globals.isGroupLeader) &&
		(workingTime.editUnit !== DurationUnit.Minutes || isWorkingTimeDateTimeChangeAllowed());
}

export function isWorkingTimeDeletable(workingTime: WorkingTime): boolean {
	const recordingMode = workingTime.workingTimeType.recordingModeUser;
	return toRecordedEntryStatus(workingTime.status) === RecordedEntryStatus.Changeable
		&& !workingTime.hasWorkingTimeRequest
		&& (workingTime.workingTimeDateSpanId == null || workingTime.workingTimeDateSpanEditable)
		// recordingMode gates direct edits/deletes regardless of editUnit
		&& recordingMode !== WorkingTimeTypeRecordingMode.BY_REQUEST
		&& (isRecordingModeUserAllowed(recordingMode) || globals.isGroupLeader)
		&& (workingTime.editUnit !== DurationUnit.Minutes || isWorkingTimeDateTimeChangeAllowed());
}

/**
 * Determines if a project time recording is resumable based on the current combined recording status,
 * the working time timeline item, and the project time record.
 *
 * @param combinedRecordingStatus The combined recording status containing the current state of working and project time recordings.
 * @param workingTimeItem The working time timeline item associated with the project time to be checked.
 * @param projectTime The project time record to be checked.
 * @returns `true` if the project time is resumable, otherwise `false`.
 */
export function isProjectTimeResumable(combinedRecordingStatus: CombinedRecordingStatus, workingTimeItem: WorkingTimeTimelineItem, projectTime: ProjectTime): boolean {
	// verify whether the recording can be continued in principle (project time is active, date time is changeable and resumable)
	if (combinedRecordingStatus?.projectTimeStatus === null || !isProjectTimeDateTimeChangeAllowed() ||
		!isRecordingResumable(combinedRecordingStatus?.projectTimeStatus?.lastProjectTime, combinedRecordingStatus?.projectTimeStatus?.runningProjectTime, projectTime)) {
		return false;
	}

	if (globals.recordingMode === RecordingMode.CLASSIC) {
		return !isWorkingTimeRunning(combinedRecordingStatus);
	} else if (globals.recordingMode === RecordingMode.STRICT || globals.recordingMode === RecordingMode.COMBINED) {
		return isNothingRunning(combinedRecordingStatus) && isTimelineSubitemProjectTimeInAttendanceTime(workingTimeItem, projectTime) ||
			isAttendanceTimeRunning(combinedRecordingStatus) && isTimelineSubitemProjectTimeInRunningAttendanceTime(workingTimeItem, projectTime, combinedRecordingStatus);
	} else {
		return false;
	}
}

export function isProjectTimeDuplicateable() {
	return isProjectTimeDateTimeChangeAllowed();
}

/**
 * Determines whether a project time timeline item's "Edit" context menu entry should be active.
 */
export function isProjectTimeEditable(projectTime: ProjectTime): boolean {
	return isProjectTimeEditAllowed({status: toRecordedEntryStatus(projectTime.status), isTaskLocked: isProjectTimeTaskLocked(projectTime)});
}

export function isProjectTimeDeletable(projectTime: ProjectTime): boolean {
	return isProjectTimeEditable(projectTime) && isProjectTimeDateTimeChangeAllowed();
}

export function setWorkingTimeStatus(workingTime: WorkingTime, statusNumber: number): JQueryXHR {
	return api("reports/updateWorkingTimeStatus.json")
		.post({
			statusNumber: statusNumber,
			idsInclude: workingTime.id,
			allSelected: false
		});
}

export function setProjectTimeStatus(projectTime: ProjectTime, statusNumber: number): JQueryXHR {
	return api("reports/updateProjectTimeStatus.json")
		.post({
			statusNumber: statusNumber,
			idsInclude: projectTime.id,
			allSelected: false
		});
}

/**
 * Determines whether the working time status number is enabled based on the provided working time and status.
 * DEVNOTE: This function is essentially a copy of <code>WorkingTimeTable#recordingChangeable</code> that works with status numbers.
 * 			Since custom maps are provided with status numbers instead of strings here (DTO), a transformation is required for it to work.
 * @param {WorkingTime} workingTime - The working time object containing the status and associated information.
 * @param {RecordedEntryStatus} status - The current recorded entry status to evaluate.
 * @return {boolean} True if the working time status number is enabled, otherwise false.
 */
export function isWorkingTimeStatusNumberEnabled(workingTime: WorkingTime, status: RecordedEntryStatus): boolean {
	const workingTimeStatus = toRecordedEntryStatus(workingTime.status);
	return status !== workingTimeStatus &&
		((status === RecordedEntryStatus.Closed || status === RecordedEntryStatus.Archived) || !workingTime.hasTimeAccount);
}

/**
 * Determines whether the project time status number is enabled based on the provided project time and status.
 *
 * @param {ProjectTime} projectTime - The project time object containing relevant properties for validation.
 * @param {RecordedEntryStatus} status - The status to be validated against the project time's status number.
 * @return {boolean} Returns true if the project time status number is enabled; otherwise, false.
 */
export function isProjectTimeStatusNumberEnabled(projectTime: ProjectTime, status: RecordedEntryStatus): boolean {
	const projectTimeStatusNumber = toRecordedEntryStatus(projectTime.status).valueOf();
	return status.valueOf() !== projectTimeStatusNumber &&
		(status.valueOf() > projectTimeStatusNumber || !isProjectTimeTaskLocked(projectTime));
}

function isRecordingResumable(lastRecording: WorkingTime | ProjectTime, runningRecording: RunningWorkingTime | RunningProjectTime, recording: WorkingTime | ProjectTime): boolean {
	return !runningRecording
		&& toRecordedEntryStatus(recording.status) === RecordedEntryStatus.Changeable
		&& lastRecording.id === recording.id
		&& isSameDay(parseISOZonedDateTime(recording.start), Temporal.Now.plainDateISO());
}

/* ----------------------------------------- quick switch/start ----------------------------------------- */
/**
 * Determines the recording procedure for a quick switch of working time based on the current combined recording status
 * and the selected working time timeline item.
 *
 * @param combinedRecordingStatus The combined recording status containing the current state of working and project time recordings.
 * @param workingTimeItem The working time timeline item to be checked.
 * @returns The recording procedure to be performed (e.g., `RecordingProcedure.Both` or `RecordingProcedure.WorkingTime`).
 */
export function getQuickSwitchWorkingTimeProcedure(combinedRecordingStatus: CombinedRecordingStatus, workingTimeItem: WorkingTimeTimelineItem): RecordingProcedure {
	if (!isWorkingTimeQuickSwitchable(combinedRecordingStatus, workingTimeItem)) {
		return;
	}

	if (combinedRecordingStatus.projectTimeStatus === null) {
		return RecordingProcedure.WorkingTime;
	} else if (globals.recordingMode === RecordingMode.STRICT && isAttendanceTimeAndProjectTimeRunning(combinedRecordingStatus) && isTimelineRecordingNonAttendanceTime(workingTimeItem)) {
		return RecordingProcedure.Both;
	} else if (globals.recordingMode === RecordingMode.COMBINED && (isAttendanceTimeAndProjectTimeRunning(combinedRecordingStatus) && (
		isTimelineRecordingNonAttendanceTime(workingTimeItem) || isTimelineRecordingAttendanceTime(workingTimeItem)))) {
		return RecordingProcedure.Both;
	} else {
		return RecordingProcedure.WorkingTime;
	}
}

/**
 * Determines the recording procedure for a quick switch of project time based on the current combined recording status,
 * the selected working time timeline item, and the project time record.
 *
 * @param combinedRecordingStatus The combined recording status containing the current state of working and project time recordings.
 * @param workingTimeItem The working time timeline item associated with the project time to be checked.
 * @param projectTime The project time record to be checked.
 * @returns The recording procedure to be performed (e.g., `RecordingProcedure.Both` or `RecordingProcedure.ProjectTime`).
 */
export function getQuickSwitchProjectTimeProcedure(combinedRecordingStatus: CombinedRecordingStatus, workingTimeItem: WorkingTimeTimelineItem, projectTime: ProjectTime): RecordingProcedure {
	if (!isProjectTimeQuickSwitchable(combinedRecordingStatus, workingTimeItem, projectTime)) {
		return;
	}

	if (globals.recordingMode === RecordingMode.STRICT || globals.recordingMode === RecordingMode.COMBINED) {
		if (isNothingRunning(combinedRecordingStatus) && isTimelineSubitemProjectTimeInAttendanceTime(workingTimeItem, projectTime)) {
			return RecordingProcedure.Both
		}
		if (isNonAttendanceTimeRunning(combinedRecordingStatus) && isTimelineSubitemProjectTimeInAttendanceTime(workingTimeItem, projectTime)) {
			return RecordingProcedure.Both
		}
		return RecordingProcedure.ProjectTime;
	}
}

/**
 * Determines if a working time is quick switchable based on the current combined recording status
 * and the selected working time timeline item.
 *
 * @param combinedRecordingStatus The combined recording status containing the current state of working and project time recordings.
 * @param workingTimeItem The working time timeline item to be checked.
 * @returns `true` if the working time is quick switchable, otherwise `false`.
 */
export function isWorkingTimeQuickSwitchable(combinedRecordingStatus: CombinedRecordingStatus, workingTimeItem: WorkingTimeTimelineItem): boolean {
	// verify if working time is active and can be quick switched/started
	if (combinedRecordingStatus.workingTimeStatus === null) {
		return false;
	}

	if (globals.recordingMode === RecordingMode.CLASSIC) {
		return true;
	} else if (globals.recordingMode === RecordingMode.STRICT) {
		return isOnlyProjectTimeRunning(combinedRecordingStatus) && isTimelineRecordingAttendanceTime(workingTimeItem) ||
			!isOnlyProjectTimeRunning(combinedRecordingStatus) && isTimelineRecordingNonAttendanceTime(workingTimeItem);
	} else if (globals.recordingMode === RecordingMode.COMBINED) {
		return !(isOnlyProjectTimeRunning(combinedRecordingStatus) && isTimelineRecordingNonAttendanceTime(workingTimeItem));
	} else {
		return false;
	}
}

/**
 * Determines if a project time is quick switchable based on the current combined recording status,
 * the working time timeline item, and the project time record.
 *
 * @param combinedRecordingStatus The combined recording status containing the current state of working and project time recordings.
 * @param workingTimeItem The working time timeline item associated with the project time to be checked.
 * @param projectTime The project time record to be checked.
 * @returns `true` if the project time is quick switchable, otherwise `false`.
 */
export function isProjectTimeQuickSwitchable(combinedRecordingStatus: CombinedRecordingStatus, workingTimeItem: WorkingTimeTimelineItem, projectTime: ProjectTime): boolean {
	// verify if project time is active and can be quick switched/started
	if (combinedRecordingStatus?.projectTimeStatus === null) {
		return false;
	}

	if (globals.recordingMode === RecordingMode.CLASSIC) {
		return true;
	} else if (globals.recordingMode === RecordingMode.STRICT || globals.recordingMode === RecordingMode.COMBINED) {
		return isNothingRunning(combinedRecordingStatus) && isTimelineSubitemProjectTimeInAttendanceTime(workingTimeItem, projectTime) ||
			isAttendanceTimeAndProjectTimeRunning(combinedRecordingStatus) ||
			isAttendanceTimeRunning(combinedRecordingStatus) ||
			isNonAttendanceTimeRunning(combinedRecordingStatus) && isTimelineSubitemProjectTimeInAttendanceTime(workingTimeItem, projectTime);
	} else {
		return false;
	}
}

/**
 * Determines whether a quick switch message should be shown for project time based on the provided message key,
 * procedure, combined recording status, and the selected working time item.
 *
 * @param messageKey The key identifying the type of quick switch message to be shown.
 * @param combinedRecordingStatus The current combined recording status containing the state of working and project time recordings.
 * @param workingTimeItem The working time timeline item associated with the project time.
 * @param projectTime The project time record to be checked.
 * @returns `true` if the quick switch message should be shown, otherwise `false`.
 */
export function showProjectTimeQuickSwitchMessage(messageKey: "timeline.quick.switch.start.BOTH" | "timeline.quick.start.BOTH" | "timeline.quick.switch" | "timeline.quick.start",
                                                  combinedRecordingStatus: CombinedRecordingStatus,
                                                  workingTimeItem: WorkingTimeTimelineItem,
                                                  projectTime: ProjectTime): boolean {
	const quickSwitchProcedure = getQuickSwitchProjectTimeProcedure(combinedRecordingStatus, workingTimeItem, projectTime);
	switch (messageKey) {
		case "timeline.quick.start": // Start '{0}'
			return (quickSwitchProcedure !== RecordingProcedure.Both || workingTimeItem.type === WorkingTimeTimelineItemType.NONE) &&
				!isProjectTimeRunning(combinedRecordingStatus);
		case "timeline.quick.switch": // Switch to '{0}' now
			return quickSwitchProcedure !== RecordingProcedure.Both &&
				isProjectTimeRunning(combinedRecordingStatus);
		case "timeline.quick.start.BOTH": // Now switch to '{0}' and start '{1}'
			return quickSwitchProcedure === RecordingProcedure.Both &&
				!isWorkingTimeRunning(combinedRecordingStatus) &&
				!isProjectTimeRunning(combinedRecordingStatus);
		case "timeline.quick.switch.start.BOTH": // Now switch to '{0}' and start '{1}'
			return quickSwitchProcedure === RecordingProcedure.Both &&
				isWorkingTimeRunning(combinedRecordingStatus) &&
				!isProjectTimeRunning(combinedRecordingStatus);
	}
}

/**
 * Determines whether a quick switch message should be shown for working time based on the provided message key and combined recording status.
 *
 * @param messageKey The key identifying the type of quick switch message to be shown.
 * @param combinedRecordingStatus The current combined recording status containing the state of working and project time recordings.
 * @param item The working time timeline item to be checked.
 * @returns `true` if the quick switch message should be shown, otherwise `false`.
 */
export function showWorkingTimeQuickSwitchMessage(messageKey: "timeline.quick.switch.stop.BOTH" | "timeline.quick.start.BOTH" | "timeline.quick.switch" | "timeline.quick.start",
                                                  combinedRecordingStatus: CombinedRecordingStatus,
                                                  item: WorkingTimeTimelineItem): boolean {
	const quickSwitchProcedure = getQuickSwitchWorkingTimeProcedure(combinedRecordingStatus, item);
	switch (messageKey) {
		case "timeline.quick.start": // Start '{0}'
			return quickSwitchProcedure !== RecordingProcedure.Both && !isWorkingTimeRunning(combinedRecordingStatus);
		case "timeline.quick.switch": // Switch to '{0}' now
			return quickSwitchProcedure !== RecordingProcedure.Both && isWorkingTimeRunning(combinedRecordingStatus);
		case "timeline.quick.switch.stop.BOTH": // Now switch to '{0}' and stop '{1}'
			return quickSwitchProcedure === RecordingProcedure.Both &&
				isWorkingTimeRunning(combinedRecordingStatus) &&
				isProjectTimeRunning(combinedRecordingStatus);
	}
}

/* Methods to match states of combined recording status with the selected timeline items/subitems.
   there is a chart with each state, see google docs sheet in TIMR-9883 */

/* ---------------------- currently running records in recording panel (represents x-axis in google sheet) ---------------------- */
export function isProjectTimeRunning(status: CombinedRecordingStatus): boolean {
	return status.projectTimeStatus && status.projectTimeStatus.runningProjectTime !== null;
}

export function isWorkingTimeRunning(status: CombinedRecordingStatus): boolean {
	return status.workingTimeStatus && status.workingTimeStatus.runningWorkingTime !== null;
}

function isNothingRunning(status: CombinedRecordingStatus): boolean {
	return !isWorkingTimeRunning(status) && !isProjectTimeRunning(status);
}

function isAttendanceTimeAndProjectTimeRunning(status: CombinedRecordingStatus): boolean {
	return isWorkingTimeRunning(status) &&
		isProjectTimeRunning(status) &&
		status.workingTimeStatus.runningWorkingTime.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime;
}

function isAttendanceTimeRunning(status: CombinedRecordingStatus): boolean {
	return isWorkingTimeRunning(status) &&
		!isProjectTimeRunning(status) &&
		status.workingTimeStatus.runningWorkingTime.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime;
}

function isNonAttendanceTimeRunning(status: CombinedRecordingStatus): boolean {
	return isWorkingTimeRunning(status) &&
		!isProjectTimeRunning(status) &&
		status.workingTimeStatus.runningWorkingTime.workingTimeType.category !== WorkingTimeTypeCategory.AttendanceTime;
}

function isOnlyProjectTimeRunning(status: CombinedRecordingStatus): boolean {
	return isProjectTimeRunning(status) && !isWorkingTimeRunning(status);
}

/* -------------------- selected timeline item/subitem (represents y-axis in google sheet) -------------------- */
function isTimelineRecordingAttendanceTime(workingTimeItem: WorkingTimeTimelineItem): boolean {
	return workingTimeItem.workingTime?.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime;
}

function isTimelineRecordingNonAttendanceTime(workingTimeItem: WorkingTimeTimelineItem): boolean {
	return workingTimeItem.workingTime?.workingTimeType.category !== WorkingTimeTypeCategory.AttendanceTime;
}

function isTimelineSubitemProjectTimeInAttendanceTime(workingTimeItem: WorkingTimeTimelineItem, projectTime: ProjectTime): boolean {
	return projectTime != null &&
		workingTimeItem.type === WorkingTimeTimelineItemType.WORKING_TIME && workingTimeItem.workingTime.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime ||
		workingTimeItem.type === WorkingTimeTimelineItemType.RUNNING && workingTimeItem.runningWorkingTime.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime;
}

function isTimelineSubitemProjectTimeInRunningAttendanceTime(workingTimeItem: WorkingTimeTimelineItem, projectTime: ProjectTime, status: CombinedRecordingStatus): boolean {
	return projectTime != null &&
		workingTimeItem.type === WorkingTimeTimelineItemType.RUNNING &&
		workingTimeItem.runningWorkingTime.id === status.workingTimeStatus.runningWorkingTime.id;
}

/**
 * Checks if there are working time types available to add a working time
 * @param config
 */
export function hasWorkingTimeTypesForWorkingTime(config: CombinedTimelineDayComponentConfig): boolean {
	return getWorkingTimeTypesForAddWorkingTime(config.workingTimeTypes, config.reportingOtherUsers).length > 0;
}

/**
 * Checks if there are working time types available to add a vacation or absence
 * @param config
 */
export function hasWorkingTimeTypesForVacation(config: CombinedTimelineDayComponentConfig): boolean {
	return getWorkingTimeTypesForAddAbsence(config.workingTimeTypes, config.reportingOtherUsers).length > 0;
}

/**
 * Checks if there are working time types available to add a working time notice
 * @param config
 */
export function hasWorkingTimeTypesForNotices(config: CombinedTimelineDayComponentConfig): boolean {
	return config.workingTimeTypesForNotices.length > 0;
}

/**
 * Checks if there are working time types available to add a working time, absence or notice
 * @private
 */
export function hasWorkingTimeTypesToAdd(config: CombinedTimelineDayComponentConfig): boolean {
	return hasWorkingTimeTypesForWorkingTime(config) || hasWorkingTimeTypesForVacation(config) || hasWorkingTimeTypesForNotices(config);
}

/**
 * Returns a custom field container element to be displayed in a timeline element
 * outerHTML must be used to insert the code directly into HTML
 * @param customFieldDefinitions list of custom field definitions
 * @param record running, recorded or requested entry
 */
export function buildCustomFields(customFieldDefinitions: CustomFieldDefinition[], record: Record | RunningEntry | WorkingTimeRequestResponseModel): HTMLDivElement {
	const customFieldContainer = document.createElement("div");
	customFieldContainer.classList.add("custom-field-container");
	for (const definition of customFieldDefinitions) {
		const value = formatCustomField(record[`customField${definition.fieldNumber}`], definition.fieldType);
		if (value) {
			const customFieldDiv = document.createElement("div");

			// escape fields that contain modifiable text to prevent XSS
			customFieldDiv.innerHTML = htmlEscape(`${definition.fieldName}: ${value}`);

			customFieldContainer.append(customFieldDiv);
		}
	}
	return customFieldContainer;
}

/**
 * Creates and returns an HTML div element configured to display a description with an accompanying icon.
 * This can be displayed in extra row like (in weekly view the space is more limited) or in column via flexbox (daily view)
 *
 * @return {HTMLDivElement} A div element containing the description text and an icon.
 * @param record The record object containing the description to be displayed.
 */
export function buildDescriptionElement(record: WorkingTime | ProjectTime | RunningWorkingTime | RunningProjectTime): HTMLElement | null {
	const escapedDescription = htmlEscape(record.description);
	if (!escapedDescription) {
		return null;
	}

	const descriptionDiv = document.createElement("div");
	descriptionDiv.classList.add("description-container");

	const icon = document.createElement("i");
	icon.classList.add("fal", "fa-comment-alt", "position-left");
	descriptionDiv.appendChild(icon);

	const span = document.createElement("span");
	span.classList.add("description-clamped");
	span.innerHTML = escapedDescription;
	descriptionDiv.appendChild(span);

	return descriptionDiv;
}

/**
 * Returns a validation table element to be displayed in a timeline element
 * Therefore, only record referenced validations are added
 * This table is colored according to the highest degree of severity
 * outerHTML must be used to insert the code directly into HTML
 * @param validationResults list of validations
 * @param recordId id of the record for which the validations are displayed
 */
export function buildValidationsTable(validationResults: ValidationResult[], recordId: number): HTMLTableElement {
	const validations = validationResults.filter(v => v.recordedEntry?.id === recordId || v.recordedEntryRef?.id === recordId);
	const table = document.createElement("table");

	if (validations.length > 0) {
		table.classList.add("validations-table", "validations-table-severity-" + getHighestSeverity(validations));

		const validationRows = validations
			.map(validation => {
				const tr = document.createElement("tr");
				tr.innerHTML = `
						<td><i class="fa fa-validation-severity-${validation.severity} position-left" title="${messages["recordingValidation.severity." + validation.severity]}"></i></td>
						<td>${htmlEscape(messages.withArguments(validation.messageCode, ...validation.messageArguments))}</td>
						`;
				return tr;
			});

		const body = document.createElement("tbody");
		body.append(...validationRows);

		table.append(body);
	}
	return table;
}

/**
 * Builds and returns a context menu div element
 * @return {HTMLDivElement} The created HTML div element, either styled as a context menu if active or empty otherwise.
 */
export function buildContextMenu(): HTMLDivElement {
	const div = document.createElement("div");
	div.classList.add("context-menu-overlay");
	div.innerHTML = `<div class="dropdown" data-context-menu-container>
						<div class="btn btn-link-primary" data-context-menu-button>
							<i class="far fa-ellipsis"></i>
						</div>
					</div>`;
	return div;
}

/**
 * Formats a time string to be displayed beside timeline items with optional timezone information e.g., 14:00 +02:00
 * @param dateTime The date time to format
 * @param showTimeZone Whether to include the timezone in the formatted string
 * @returns HTML string with formatted time and timezone (if enabled)
 * @private
 */
export function buildTimeString(dateTime: Temporal.ZonedDateTime | null, showTimeZone: boolean): HTMLSpanElement {
	const span = document.createElement("span");
	span.innerHTML = formatTime(dateTime);

	if (showTimeZone) {
		const timeZoneSpan = document.createElement("span");
		timeZoneSpan.classList.add("text-timezone");
		timeZoneSpan.innerHTML = dateTime?.offset ?? "";
		span.appendChild(timeZoneSpan);
	}

	return span;
}

/**
 * Builds the HTML string for timeline items and subitems, e.g.:
 * record (ascending with timezone):
 * 		o 14:00 +02:00
 * 		o 15:00 +02:00
 * record (ascending):
 * 		o 14:00
 * 		o 15:00
 * record (descending:
 * 		o 15:00
 * 		o 14:00
 * running (ascending):
 * 		o 14:00
 * 		o now
 * running (descending):
 * 		o now
 * 		o 14:00
 */
export function buildTimelineTimes(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null, showTimeZone: boolean, timelineSortDescending: boolean): string {
	const startTime = buildTimeString(start, showTimeZone).outerHTML;

	// if no end time is provided, "now" is shown instead of the time
	const endTime = end ? buildTimeString(end, showTimeZone).outerHTML : `<span>${messages["timeline.now"]}</span>`;

	// return span containing sorted start and end time string
	return `<span class="times">${(timelineSortDescending ? `${endTime + startTime}` : `${startTime + endTime}`)}</span>`;
}
